import React, { useEffect, useState } from "react";
import {
  CartesianGrid,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  BarChart,
  Bar,
  Legend,
  Rectangle,
} from "recharts";
import api from "../../globals/axios";
import { useTranslation } from "react-i18next";
import { Box, CircularProgress, Typography } from "@mui/material";
import { ErrorOutline, InfoOutlined } from "@mui/icons-material";
import { isNotNullOrEmptyArray } from "../../globals/utils";
import PropTypes from "prop-types";
import { getSevenDayIrrigationPlanner } from "../../api/amSatelliteReport";
import { calculateIrrigationPlannerSummary } from "./utils";
import AMIrrigationPlannerSummary from "./AMIrrigationPlannerSummary";

const AMIrrigationPlanner = ({ farmId, accessKey }) => {
  const [planData, setPlanData] = useState([]);
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  const fetchData = async () => {
    try {
      let data;
      if (accessKey) {
        data = await getSevenDayIrrigationPlanner(farmId, accessKey);
      } else {
        const response = await api.get(
          `/satellitedata/get-seven-day-irrigation-planner-v2/${farmId}`
        );
        data = response.data;
      }
      if (data && isNotNullOrEmptyArray(data.data)) {
        data.data.forEach((day) => {
          day.irrigation_liters_acre = Math.round(
            (day.irrigation_mm || 0) * 4047
          ); // Convert mm to liters per acre
          day.rainfall_liters_acre = Math.round((day.rainfall_mm || 0) * 4047); // Convert mm to liters per acre
          day;
        });
        setPlanData(data.data || []);
        setIsError(false);
      } else {
        setIsError(true);
      }
    } catch (error) {
      console.error("Error fetching Irrigation details:", error);
      setIsError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          my: 4,
          py: 3,
        }}
      >
        <CircularProgress
          size={40}
          thickness={4}
          sx={{ mb: 2, color: "primary.main" }}
        />
        <Typography variant="h6" color="text.secondary">
          {t("Loading Irrigation planner")}...
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {t("This may take a moment")}
        </Typography>
      </Box>
    );
  }

  if (isError) {
    return (
      <Box
        sx={{
          p: 3,
          my: 2,
          border: "1px solid #f5c6cb",
          borderRadius: 2,
          bgcolor: "#f8d7da",
          display: "flex",
          alignItems: "center",
        }}
      >
        <ErrorOutline sx={{ color: "#721c24", mr: 2, fontSize: 28 }} />
        <Box>
          <Typography
            variant="h6"
            sx={{ color: "#721c24", fontWeight: "bold", mb: 0.5 }}
          >
            {t("Unable to fetch Irrigation Planner")}
          </Typography>
        </Box>
      </Box>
    );
  }
  if (!isNotNullOrEmptyArray(planData)) {
    return (
      <Box
        sx={{
          p: 3,
          my: 2,
          border: "1px solid #f5c6cb",
          borderRadius: 2,
          bgcolor: "#f8d7da",
          display: "flex",
          alignItems: "center",
        }}
      >
        <InfoOutlined sx={{ color: "#721c24", mr: 2, fontSize: 28 }} />
        <Box>
          <Typography
            variant="h6"
            sx={{ color: "#721c24", fontWeight: "bold", mb: 0.5 }}
          >
            {t("No irrigation plan data available")}
          </Typography>
        </Box>
      </Box>
    );
  }

  const {
    irrigationDates,
    totalIrrigationNeeded,
    totalIrrigationConverted,
    totalRainfall,
    totalRainfallConverted,
  } = calculateIrrigationPlannerSummary(planData, t);

  return (
    <Box>
      {/* Summary Section */}
      <AMIrrigationPlannerSummary
        irrigationDates={irrigationDates}
        totalIrrigationConverted={totalIrrigationConverted}
        totalRainfallConverted={totalRainfallConverted}
        totalIrrigationNeeded={totalIrrigationNeeded}
        totalRainfall={totalRainfall}
      />

      {/* Chart Section */}
      <ResponsiveContainer width="100%" height={350}>
        <BarChart
          width="100%"
          height={300}
          data={planData}
          margin={{ top: 5, right: 30, left: 20, bottom: 10 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="date"
            angle={45}
            height={70}
            dy={20}
            interval="preserveStartEnd"
            tick={{ fontSize: 12 }}
            tickFormatter={(dateStr) => {
              const date = new Date(dateStr);
              return date.toLocaleDateString(undefined, {
                month: "short",
                day: "numeric",
              });
            }}
          />
          <YAxis />
          <Tooltip />
          <Legend />
          <Bar
            dataKey="irrigation_liters_acre"
            name={t("Watering Needed (L/acre)")}
            fill="#8884d8"
            activeBar={<Rectangle fill="pink" stroke="blue" />}
            isAnimationActive={!accessKey}
          />
          <Bar
            dataKey="rainfall_liters_acre"
            name={t("Expected Rain (L/acre)")}
            fill="#82ca9d"
            activeBar={<Rectangle fill="gold" stroke="green" />}
            isAnimationActive={!accessKey}
          />
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
};

AMIrrigationPlanner.propTypes = {
  farmId: PropTypes.string.isRequired,
};

export default AMIrrigationPlanner;
